import {
    useContext,
    type FC
} from "react";
import {
    View
} from "react-native";
import type ITraceToolCallProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    getTraceDurationText,
    getTraceCodeText,
    joinTraceParts,
    useTraceOpen
} from "../../util";
import {
    TraceContext
} from "../../context";
import TraceCodeBlock from "../../internal/codeBlock";
import TraceCollapsible from "../collapsible";
import TraceHead from "../../internal/head";
import TraceContent from "../content";
import TraceTrigger from "../trigger";
import Alert from "../../../alert";
import Text from "../../../text";
import TraceLine from "../line";

const TraceToolCall: FC<ITraceToolCallProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    isOpen: isOpenProp,
    status = "running",
    errorDescription,
    isDefaultOpen,
    onOpenChange,
    onRetryPress,
    description,
    onStopPress,
    errorTitle,
    duration,
    output,
    input,
    style,
    title,
    icon,
    name,
    ...props
}) => {
    const traceContext = useContext(TraceContext);

    const customLocalize = customLocalizeProp ?? traceContext?.customLocalize;

    const customTheme = customThemeProp ?? traceContext?.customTheme;

    const {
        typography,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isRunning = status === "running";

    const isError = status === "error";

    const {
        handleOpenChange,
        isOpen
    } = useTraceOpen({
        isOpen: isOpenProp,
        isDefaultOpen,
        onOpenChange,
        isRunning
    });

    const {
        meta: metaDynamicStyle,
        name: nameDynamicStyle
    } = useStyles({
        typography,
        spaces
    });

    const durationText = duration === undefined ? undefined : getTraceDurationText({
        seconds: duration / 1000,
        locale: activeLocale,
        localize
    });

    const hasContent = !!name || input !== undefined || output !== undefined || isError;

    const stopHandler = isRunning ? onStopPress : undefined;

    const renderLine = () => {
        return <TraceLine
            description={joinTraceParts({
                parts: [
                    description,
                    durationText
                ]
            })}
            customTheme={customTheme}
            status={status}
            title={title}
            icon={icon}
        />;
    };

    const renderMeta = () => {
        if(!name) {
            return null;
        }

        return <View
            style={[
                stylesheet.meta,
                metaDynamicStyle
            ]}
        >
            <Text
                customTheme={customTheme}
                variant="bodySmallSize"
                color="low"
            >
                {localize("tool")}
            </Text>
            <Text
                customTheme={customTheme}
                style={nameDynamicStyle}
                variant="monoMediumSize"
                color="mid"
            >
                {name}
            </Text>
            {
                durationText ?
                    <>
                        <Text
                            customTheme={customTheme}
                            variant="bodySmallSize"
                            color="low"
                        >
                            ·
                        </Text>
                        <Text
                            customTheme={customTheme}
                            variant="bodySmallSize"
                            color="low"
                        >
                            {durationText}
                        </Text>
                    </>
                :
                    null
            }
        </View>;
    };

    const renderInput = () => {
        if(input === undefined) {
            return null;
        }

        return <TraceCodeBlock
            copyLabel={localize("copy-input")}
            customLocalize={customLocalize}
            label={localize("input")}
            customTheme={customTheme}
            text={getTraceCodeText({
                value: input
            })}
        />;
    };

    const renderOutput = () => {
        if(isError) {
            return <Alert
                title={errorTitle ?? localize("tool-call-failed")}
                actions={onRetryPress ? [
                    {
                        title: localize("try-again"),
                        onPress: onRetryPress
                    }
                ] : undefined}
                customLocalize={customLocalize}
                description={errorDescription}
                customTheme={customTheme}
                style={stylesheet.alert}
                type="danger"
            />;
        }

        if(output === undefined) {
            return null;
        }

        return <TraceCodeBlock
            copyLabel={localize("copy-output")}
            customLocalize={customLocalize}
            label={localize("output")}
            customTheme={customTheme}
            text={getTraceCodeText({
                value: output
            })}
        />;
    };

    if(!hasContent) {
        return <View
            {...props}
            style={[
                style,
                stylesheet.container
            ]}
        >
            <TraceHead
                customLocalize={customLocalize}
                customTheme={customTheme}
                onStopPress={stopHandler}
            >
                {renderLine()}
            </TraceHead>
        </View>;
    }

    return <TraceCollapsible
        {...props}
        onOpenChange={handleOpenChange}
        customTheme={customTheme}
        isOpen={isOpen}
        style={style}
    >
        <TraceTrigger
            customLocalize={customLocalize}
            customTheme={customTheme}
            onStopPress={stopHandler}
        >
            {renderLine()}
        </TraceTrigger>
        <TraceContent
            customTheme={customTheme}
        >
            {renderMeta()}
            {renderInput()}
            {renderOutput()}
        </TraceContent>
    </TraceCollapsible>;
};
export default TraceToolCall;
